import { Helmet } from 'react-helmet-async'
import { useTranslation, Trans } from "react-i18next"
import _ from 'lodash'

import { Navigate, useSearchParams, Link, Outlet } from '../navigation'
import { useRoles } from '../user/Auth'
import { competitorFamilyUrl, competitorMemberUrl } from './utils'
import { PlusCircleIcon } from '@heroicons/react/24/outline'
import EmptyState from '../components/EmptyState'
import { useExternal } from './'
import { useFilteredExternal } from '../filter/FilteredExternal'
import { BreadCrumbs } from '../components/BreadCrumbs'
import { CompetitorFamily, CompetitorMember } from './external'
import { competitor_family } from '../data'
import { PlainImage } from '../components/Image'

const pageSize = 50

export default function Portfolio() {
    const { t } = useTranslation()
    const { isEditUser } = useRoles()

    const { families: allFamilies } = useExternal()
    const { families, membersByFamilyId } = useFilteredExternal()

    const [params] = useSearchParams()
    const page = parseInt(params.get('page') ?? '1')

    const sortedFamilies = _(families).sortBy(f => f.internalReference).reverse().value()
    const shownFamilies = sortedFamilies.slice((page - 1) * pageSize, page * pageSize)
    const maxPage = Math.ceil(families.length / pageSize)

    const isFullyEmpty = allFamilies?.length === 0

    if (isFullyEmpty) {
        return (
            <>
                {/* @ts-ignore */}
                <Helmet>
                    <title>{t('external')} | Patent Cockpit</title>
                </Helmet>
                <div className="portfolio-menu">
                    <h2>{t('external')}</h2>
                </div>
                <div className='main-content'>
                    <div className='max-w-screen-lg'>
                        <EmptyState title={t('add-competitor-family')} text={t('add-competitor-family-text')} items={[
                            {
                                title: t('add-competitor-family'),
                                description: t('add-competitor-family-text'),
                                icon: PlusCircleIcon,
                                background: 'bg-pcx-700',
                                to: 'family/add',
                            },
                        ]} />
                    </div>
                </div>
                {isEditUser &&
                    <div className="fixed bottom-4 right-4">
                        <Link className="btn-primary py-2 px-4" to="family/add">
                            {t('add-competitor-family')}
                        </Link>
                    </div>
                }
                <Outlet />
            </>
        )
    } else if (page > 1 && shownFamilies.length === 0) {
        return <Navigate to={`?page=1`} />
    }

    return (
        <>
            {/* @ts-ignore */}
            <Helmet>
                <title>{t('external')} | Patent Cockpit</title>
            </Helmet>
            <div className="py-0 pl-0 portfolio-menu pr-0">
                <div className="grow max-w-screen-lg flex flex-row items-start sm:items-center sm:gap-2 mr-4">
                    <BreadCrumbs parts={[t('external')]} />
                    <div className='grow' />
                    <div className='flex flex-col max-sm:py-1 max-sm:items-end sm:flex-row gap-2'>
                        {isEditUser &&
                            <Link className="btn-secondary py-0.5 text-sm" to="family/add">
                                {t('add-competitor-family')}
                            </Link>
                        }
                    </div>
                </div>
            </div>
            <div key={page} className="overflow-auto grow pl-2 sm:pl-4">
                <div className="max-w-screen-lg pr-2 sm:pr-4">
                    <div className="flex flex-col gap-2">
                        {shownFamilies.map(family =>
                            <FamilyRow
                                key={family.internalReference}
                                family={family}
                                members={membersByFamilyId[family.competitorFamilyId] ?? []}
                            />
                        )}
                        {shownFamilies.length !== sortedFamilies.length &&
                            <Pagination {...{ page, maxPage, maxCount: sortedFamilies.length }} />
                        }
                    </div>
                </div>
            </div>
            <Outlet />
        </>
    )
}

function Pagination({ page, maxPage, maxCount }) {
    const { t } = useTranslation()

    const start = (page - 1) * pageSize + 1
    const end = Math.min(page * pageSize, maxCount)

    return (
        <nav aria-label="Pagination" className="flex items-center justify-between py-3 px-1">
            <div className="hidden sm:block">
                <p className="text-sm text-gray-700">
                    <Trans i18nKey="showing_from_to_of" values={{ start, end, maxCount }} components={{ b: <span className='font-medium' /> }} />
                </p>
            </div>
            <div className="flex flex-1 gap-3 justify-between sm:justify-end">
                <PageButton disabled={page === 1} to={`.?page=${page - 1}`}>{t('prev-page')}</PageButton>
                <PageButton disabled={page === maxPage} to={`.?page=${page + 1}`}>{t('next-page')}</PageButton>
            </div>
        </nav>
    )
}

function PageButton({ disabled, to, children }) {
    return disabled
        ? <div className='relative inline-flex items-center rounded-md bg-transparent px-3 py-2 text-sm font-semibold text-gray-500 ring-1 ring-inset ring-gray-200'>
            {children}
        </div>
        : <Link
            className="relative inline-flex items-center rounded-md bg-transparent px-3 py-2 text-sm font-semibold text-pcx-500 hover:text-white ring-1 ring-inset ring-gray-300 hover:bg-pcx-600 hover:ring-pcx-600 focus-visible:outline-offset-0"
            to={to} onClick={() => window.scrollTo(0, 0)} >
            {children}
        </Link>
}

function CountryMemberLinks({ members }: { members: CompetitorMember[] }) {
    const { t } = useTranslation()
    // Show members with 'relevant' status as country links
    const relevantMembers = members.filter(m => m.competitorMemberStatus === 'relevant')
    const grouped = _.sortBy(Object.entries(_.groupBy(relevantMembers, m => m.countryCode)), ([cc]) => cc)

    return (
        <div className="flex flex-wrap gap-1 text-sm sm:mb-1 mr-1 sm:mr-0">
            {_.flatMap(grouped, ([countryCode, ms]) =>
                _.map(ms, m =>
                    <Link
                        key={m.internalReference}
                        className="ribbon"
                        to={competitorMemberUrl(m)}
                        title={`${m.countryCode} - ${t(m.competitorMemberStatus)}`}
                    >
                        {countryCode}
                    </Link>
                )
            )}
        </div>
    )
}

function FamilyRow({ family, members }: { family: CompetitorFamily; members: CompetitorMember[] }) {
    return (
        <div
            id={encodeURIComponent(family.internalReference)}
            tabIndex={-1}
            className="p-4 bg-white rounded-xl shadow flex flex-col lg:grid lg:grid-cols-[1fr_250px] gap-2 min-w-0 max-w-full focus:outline-none lg:h-44"
        >
            <div className="flex flex-row gap-2 w-full min-w-0">
                <div className="sm:w-48 w-24 h-24 shrink-0">
                    <PlainImage {...{
                        entity: competitor_family,
                        entityId: family.competitorFamilyId,
                        title: `${family.internalReference}: ${family.familyName}`,
                        text: family.summary,
                    }} />
                </div>
                <Link className="overflow-hidden min-w-0" to={competitorFamilyUrl(family)}>
                    <div className="flex flex-col gap-1">
                        <h4 className="whitespace-nowrap truncate">
                            {family.internalReference}: {family.familyName}
                        </h4>
                        <div title={family.summary} className="text-gray-700 text-ellipsis h-fit line-clamp-4 whitespace-pre-wrap overflow-hidden text-sm sm:text-base">
                            {family.summary}
                        </div>
                    </div>
                </Link>
            </div>
            <div className="lg:border-l-2 border-pc-200 flex flex-row items-center lg:flex-col lg:items-start max-lg:pt-2 lg:pl-2">
                <div className="overflow-auto lg:h-36">
                    <CountryMemberLinks members={members} />
                </div>
            </div>
        </div>
    )
}
